import React, { useState, useEffect, useCallback } from 'react';
import { Users, Mail, Download, MessageSquare, TrendingUp, Clock, BarChart3, FlaskConical, Ticket, BookOpen, LogOut, Crown, Database } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import BlogTab from './admin/BlogTab';
import OverviewTab from './admin/OverviewTab';
import SubscribersTab from './admin/SubscribersTab';
import ContactsTab from './admin/ContactsTab';
import DownloadsTab from './admin/DownloadsTab';
import SchedulerTab from './admin/SchedulerTab';
import AnalyticsTab from './admin/AnalyticsTab';
import ABTestTab from './admin/ABTestTab';
import CouponsTab from './admin/CouponsTab';
import MemberManagement from './admin/MemberManagement';
import WhatsAppTab from './admin/WhatsAppTab';
import BackupTab from './admin/BackupTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, isAdmin, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerLogs, setSchedulerLogs] = useState(null);
  const [blogSchedulerStatus, setBlogSchedulerStatus] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [blogTriggering, setBlogTriggering] = useState(false);
  const [blogTriggerResult, setBlogTriggerResult] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [abResults, setAbResults] = useState(null);
  const [couponData, setCouponData] = useState(null);
  const [vipEmail, setVipEmail] = useState('');
  const [vipCreating, setVipCreating] = useState(false);
  const [vipResult, setVipResult] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [growthData, setGrowthData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const exportCSV = (type) => {
    window.open(`${BACKEND_URL}/api/admin/export/${type}`, '_blank');
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, subscribersRes, contactsRes, downloadsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`),
        axios.get(`${BACKEND_URL}/api/admin/subscribers?limit=10`),
        axios.get(`${BACKEND_URL}/api/admin/contacts?limit=10`),
        axios.get(`${BACKEND_URL}/api/admin/downloads?limit=10`)
      ]);
      setStats(statsRes.data);
      setSubscribers(subscribersRes.data.subscribers);
      setContacts(contactsRes.data.contacts);
      setDownloads(downloadsRes.data.downloads);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSchedulerData = useCallback(async () => {
    try {
      const [statusRes, logsRes, blogSchedRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/scheduler/status`),
        axios.get(`${BACKEND_URL}/api/admin/scheduler/logs`),
        axios.get(`${BACKEND_URL}/api/blog/admin/scheduler-status`),
      ]);
      setSchedulerStatus(statusRes.data);
      setSchedulerLogs(logsRes.data);
      setBlogSchedulerStatus(blogSchedRes.data);
    } catch (error) {
      console.error('Error fetching scheduler data:', error);
    }
  }, []);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      const [analyticsRes, abRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/dashboard?days=30`),
        axios.get(`${BACKEND_URL}/api/analytics/ab-results/hero_headline`)
      ]);
      setAnalyticsData(analyticsRes.data);
      setAbResults(abRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []);

  const fetchCouponData = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/coupons/admin/list`);
      setCouponData(res.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  }, []);

  const handleCreateVIP = async (e) => {
    e.preventDefault();
    if (!vipEmail.trim()) return;
    setVipCreating(true);
    setVipResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/coupons/admin/create-vip`, { email: vipEmail.trim() });
      setVipResult({ success: true, message: res.data.message, code: res.data.coupon?.code });
      setVipEmail('');
      fetchCouponData();
    } catch (err) {
      setVipResult({ success: false, message: err.response?.data?.detail || 'Gagal membuat VIP kupon' });
    } finally {
      setVipCreating(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'overview') {
      Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/conversion`),
        axios.get(`${BACKEND_URL}/api/admin/analytics/growth?days=14`),
      ]).then(([convRes, growthRes]) => {
        setConversionData(convRes.data);
        setGrowthData(growthRes.data);
      }).catch(() => {});
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'scheduler') {
      fetchSchedulerData();
      const interval = setInterval(fetchSchedulerData, 15000);
      return () => clearInterval(interval);
    }
  }, [activeTab, fetchSchedulerData]);

  useEffect(() => {
    if (activeTab === 'analytics' || activeTab === 'ab-test') {
      fetchAnalyticsData();
    }
  }, [activeTab, fetchAnalyticsData]);

  useEffect(() => {
    if (activeTab === 'coupons') {
      fetchCouponData();
    }
  }, [activeTab, fetchCouponData]);

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/scheduler/trigger`);
      setTriggerResult(res.data);
      await fetchSchedulerData();
    } catch (error) {
      setTriggerResult({ status: 'error', reason: error.response?.data?.detail || 'Gagal trigger scheduler' });
    } finally {
      setTriggering(false);
    }
  };

  const handleBlogTrigger = async () => {
    setBlogTriggering(true);
    setBlogTriggerResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/blog/admin/trigger-generate`);
      setBlogTriggerResult(res.data);
      // Poll for updated status after a short delay
      setTimeout(() => fetchSchedulerData(), 5000);
    } catch (error) {
      setBlogTriggerResult({ status: 'error', message: error.response?.data?.detail || 'Gagal trigger artikel' });
    } finally {
      setBlogTriggering(false);
    }
  };

  if (loading && !stats) {
    return <div className="admin-loading">Loading dashboard...</div>;
  }

  // Auth gate — redirect if not admin
  if (!authLoading && (!user || !isAdmin)) {
    return (
      <div className="admin-loading" style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:'1rem'}}>
        <Crown size={40} color="#d9fb06" />
        <h2 style={{color:'#EDEDED'}}>Akses Admin Diperlukan</h2>
        <p style={{color:'#71717A',marginBottom:'1rem'}}>Silakan login sebagai admin untuk mengakses dashboard.</p>
        <button onClick={() => navigate('/login')} style={{padding:'0.75rem 2rem',borderRadius:'0.5rem',border:'none',cursor:'pointer',background:'#d9fb06',color:'#0A0A0A',fontWeight:600,fontSize:'0.9rem'}} data-testid="admin-login-redirect">
          Login sebagai Admin
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'subscribers', label: 'Subscribers' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'downloads', label: 'Downloads', testId: 'admin-tab-downloads' },
    { id: 'scheduler', label: 'Scheduler', icon: <Clock size={16} />, testId: 'admin-tab-scheduler' },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={16} />, testId: 'admin-tab-analytics' },
    { id: 'ab-test', label: 'A/B Test', icon: <FlaskConical size={16} />, testId: 'admin-tab-abtest' },
    { id: 'coupons', label: 'Kupon', icon: <Ticket size={16} />, testId: 'admin-tab-coupons' },
    { id: 'blog', label: 'Blog', icon: <BookOpen size={16} />, testId: 'admin-tab-blog' },
    { id: 'members', label: 'Members', icon: <Users size={16} />, testId: 'admin-tab-members' },
    { id: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare size={16} />, testId: 'admin-tab-whatsapp' },
    { id: 'backup', label: 'Backup', icon: <Database size={16} />, testId: 'admin-tab-backup' },
  ];

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Admin Dashboard</h1>
          <p className="admin-subtitle">Behavioral Finance & Wealth Logic</p>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'1rem'}}>
          <span style={{fontSize:'0.85rem',color:'#A1A1AA'}}>{user?.name || user?.email}</span>
          <Link to="/member" style={{fontSize:'0.8rem',color:'#d9fb06',textDecoration:'none'}}>Member Area</Link>
          <button onClick={async () => { await logout(); navigate('/login'); }} style={{display:'flex',alignItems:'center',gap:'0.25rem',background:'none',border:'none',color:'#71717A',cursor:'pointer',fontSize:'0.8rem'}} data-testid="admin-logout">
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => setActiveTab('subscribers')} style={{cursor:'pointer'}} data-testid="stat-card-subscribers">
          <div className="stat-icon" style={{background: '#d9fb06'}}><Users size={24} color="#1a1c1b" /></div>
          <div className="stat-content">
            <h3 className="stat-value">{stats?.subscribers?.total || 0}</h3>
            <p className="stat-label">Total Subscribers</p>
            <p className="stat-change">+{stats?.subscribers?.new_today || 0} today</p>
          </div>
        </div>
        <div className="stat-card" onClick={() => setActiveTab('downloads')} style={{cursor:'pointer'}} data-testid="stat-card-downloads">
          <div className="stat-icon" style={{background: '#3f4816'}}><Download size={24} color="#d9fb06" /></div>
          <div className="stat-content">
            <h3 className="stat-value">{stats?.downloads?.total || 0}</h3>
            <p className="stat-label">Preview Downloads</p>
            <p className="stat-change">{stats?.downloads?.unique_users || 0} unique users</p>
          </div>
        </div>
        <div className="stat-card" onClick={() => setActiveTab('contacts')} style={{cursor:'pointer'}} data-testid="stat-card-contacts">
          <div className="stat-icon" style={{background: '#d9fb06'}}><MessageSquare size={24} color="#1a1c1b" /></div>
          <div className="stat-content">
            <h3 className="stat-value">{stats?.contacts?.total || 0}</h3>
            <p className="stat-label">Contact Messages</p>
            <p className="stat-change">{stats?.contacts?.unread || 0} unread</p>
          </div>
        </div>
        <div className="stat-card" onClick={() => setActiveTab('analytics')} style={{cursor:'pointer'}} data-testid="stat-card-growth">
          <div className="stat-icon" style={{background: '#3f4816'}}><TrendingUp size={24} color="#d9fb06" /></div>
          <div className="stat-content">
            <h3 className="stat-value">{stats?.subscribers?.new_this_week || 0}</h3>
            <p className="stat-label">This Week</p>
            <p className="stat-change">New subscribers</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            data-testid={tab.testId}
          >
            {tab.icon && <span style={{marginRight: '6px', verticalAlign: 'middle', display: 'inline-flex'}}>{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="admin-content">
        {activeTab === 'overview' && <OverviewTab stats={stats} conversionData={conversionData} growthData={growthData} exportCSV={exportCSV} />}
        {activeTab === 'subscribers' && <SubscribersTab subscribers={subscribers} exportCSV={exportCSV} />}
        {activeTab === 'contacts' && <ContactsTab contacts={contacts} exportCSV={exportCSV} />}
        {activeTab === 'downloads' && <DownloadsTab downloads={downloads} />}
        {activeTab === 'scheduler' && <SchedulerTab schedulerStatus={schedulerStatus} schedulerLogs={schedulerLogs} triggering={triggering} triggerResult={triggerResult} handleTrigger={handleTrigger} blogSchedulerStatus={blogSchedulerStatus} blogTriggering={blogTriggering} blogTriggerResult={blogTriggerResult} handleBlogTrigger={handleBlogTrigger} />}
        {activeTab === 'analytics' && <AnalyticsTab analyticsData={analyticsData} />}
        {activeTab === 'ab-test' && <ABTestTab abResults={abResults} />}
        {activeTab === 'coupons' && <CouponsTab couponData={couponData} vipEmail={vipEmail} setVipEmail={setVipEmail} vipCreating={vipCreating} vipResult={vipResult} handleCreateVIP={handleCreateVIP} />}
        {activeTab === 'blog' && <BlogTab />}
        {activeTab === 'members' && <MemberManagement />}
        {activeTab === 'whatsapp' && <WhatsAppTab />}
        {activeTab === 'backup' && <BackupTab />}
      </div>
    </div>
  );
};

export default AdminDashboard;
